import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine, Keyboard, CameraOff } from "lucide-react";

const REGION_ID = "groot-barcode-region";

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: null, at: 0 });
  const [camError, setCamError] = useState(false);
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return;
    setCamError(false);
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 12, qrbox: { width: 260, height: 160 }, aspectRatio: 1.6 },
          (decoded) => {
            const now = Date.now();
            if (decoded === lastRef.current.code && now - lastRef.current.at < 2500) return;
            lastRef.current = { code: decoded, at: now };
            onDetected(decoded);
          },
          () => {}
        );
        if (cancelled) await scanner.stop().catch(() => {});
      } catch (e) {
        setCamError(true);
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open, onDetected]);

  const submitManual = () => {
    const code = manual.trim();
    if (code.length < 3) return;
    onDetected(code);
    setManual("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ScanLine className="h-5 w-5 text-brand-green" /> Barkod Tara
          </DialogTitle>
          <DialogDescription>El terminaliyle (USB/Bluetooth) okutabilir, kamerayı kullanabilir veya barkodu elle girebilirsiniz.</DialogDescription>
        </DialogHeader>

        {camError ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <CameraOff className="h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-500">Kamera açılamadı (izin yok veya cihaz desteklemiyor). El terminaliyle okutun ya da barkodu aşağıya elle girin.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md bg-black">
            <div id={REGION_ID} className="w-full [&_video]:w-full" />
          </div>
        )}

        <div className="mt-1">
          <label className="overline mb-1 flex items-center gap-1"><Keyboard className="h-3.5 w-3.5" /> Elle / El Terminali ile Barkod</label>
          <div className="flex gap-2">
            <Input data-testid="barcode-manual-input" value={manual} autoFocus
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
              placeholder="Barkodu okutun / yazın ve Enter" className="font-mono" />
            <Button data-testid="barcode-manual-submit" onClick={submitManual} className="shrink-0 bg-brand-green text-white hover:bg-brand-green-600">Ekle</Button>
          </div>
        </div>
        <p className="text-center text-[11px] text-slate-400">El terminalleri klavye gibi çalışır; imleç bu kutudayken okutmanız yeterli.</p>
      </DialogContent>
    </Dialog>
  );
}
